import React from "react";
import Select from "react-select";
import { useFiltrosGlobais } from "../providers/FiltrosProvider";

interface FiltroProps {
    onCorporationChange?: (corpId: number | null) => void;
    onOrganizationChange?: (orgs: string[]) => void;
    disabled?: boolean;
    placeholder?: {
        corporation?: string;
        organization?: string;
    };
    style?: {
        corporation?: React.CSSProperties;
        organization?: React.CSSProperties;
    };
}

export const FiltroGlobal: React.FC<FiltroProps> = ({
    onCorporationChange,
    onOrganizationChange,
    disabled = false,
    placeholder = {},
    style = {}
}) => {
    const {
        filtrosGlobais,
        setSelectedCorporation,
        setSelectedOrganizations,
        isInitialized
    } = useFiltrosGlobais();

    const handleCorporationChange = (selectedOption: any) => {
        const corpId = selectedOption ? selectedOption.value : null;

        // Atualizar estado global
        setSelectedCorporation(corpId);

        // Callback opcional para a página
        if (onCorporationChange) {
            onCorporationChange(corpId);
        }
    };

    const handleOrganizationChange = (selectedOptions: any) => {
        const selectedOrgs = selectedOptions
            ? selectedOptions.map((opt: any) => opt.value)
            : [];

        // Atualizar estado global
        setSelectedOrganizations(selectedOrgs);

        // Callback opcional para a página
        if (onOrganizationChange) {
            onOrganizationChange(selectedOrgs);
        }
    };

    // Filtrar organizações pela corporação selecionada
    const filteredOrganizations = filtrosGlobais.selectedCorporation
        ? filtrosGlobais.availableOrganizations.filter(
            org => org.Idcorporacao === filtrosGlobais.selectedCorporation
        )
        : filtrosGlobais.availableOrganizations;

    if (!isInitialized) {
        return (
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <div style={{ flex: 1, maxWidth: "300px", ...style.corporation }}>
                    <Select
                        placeholder={placeholder.corporation || "Carregando corporações..."}
                        isDisabled={true}
                        classNamePrefix="react-select"
                    />
                </div>
                <div style={{ flex: 1, maxWidth: "300px", ...style.organization }}>
                    <Select
                        placeholder={placeholder.organization || "Carregando organizações..."}
                        isDisabled={true}
                        classNamePrefix="react-select"
                    />
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {/* Filtro de Corporações */}
            <div style={{ flex: 1, maxWidth: "300px", ...style.corporation }}>
                <Select
                    options={filtrosGlobais.availableCorporations
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((corp) => ({
                        label: corp.name,
                        value: corp.id,
                    }))}
                    value={
                        filtrosGlobais.selectedCorporation && filtrosGlobais.availableCorporations.length > 0
                            ? {
                                label: filtrosGlobais.availableCorporations.find(
                                    corp => corp.id === filtrosGlobais.selectedCorporation
                                )?.name || '',
                                value: filtrosGlobais.selectedCorporation
                            }
                            : null
                    }
                    onChange={handleCorporationChange}
                    placeholder={placeholder.corporation || "Filtrar por Corporação"}
                    isClearable
                    isDisabled={disabled}
                    classNamePrefix="react-select"
                />
            </div>

            {/* Filtro de Organizações */}
            <div style={{ flex: 1, maxWidth: "300px", ...style.organization }}>
                <Select
                    isMulti
                    options={filteredOrganizations.map((org) => ({
                        label: org.OrganizationName,
                        value: org.OrganizationName,
                    }))}
                    value={filteredOrganizations
                        .filter((org) => filtrosGlobais.selectedOrganizations.includes(org.OrganizationName))
                        .map((org) => ({ label: org.OrganizationName, value: org.OrganizationName }))}
                    onChange={handleOrganizationChange}
                    placeholder={placeholder.organization || "Filtrar por Organização"}
                    isClearable
                    isDisabled={disabled}
                    classNamePrefix="react-select"
                />
            </div>
        </div>
    );
};

// Componente individual para apenas o filtro de corporação
export const FiltroCorporacao: React.FC<{
    onCorporationChange?: (corpId: number | null) => void;
    disabled?: boolean;
    placeholder?: string;
    style?: React.CSSProperties;
}> = ({
    onCorporationChange,
    disabled = false,
    placeholder = "Filtrar por Corporação",
    style = {}
}) => {
        const {
            filtrosGlobais,
            setSelectedCorporation,
            isInitialized
        } = useFiltrosGlobais();

        const handleCorporationChange = (selectedOption: any) => {
            const corpId = selectedOption ? selectedOption.value : null;

            setSelectedCorporation(corpId);

            if (onCorporationChange) {
                onCorporationChange(corpId);
            }
        };

        if (!isInitialized) {
            return (
                <div style={{ flex: 1, maxWidth: "300px", ...style }}>
                    <Select
                        placeholder="Carregando corporações..."
                        isDisabled={true}
                        classNamePrefix="react-select"
                    />
                </div>
            );
        }

        return (
            <div style={{ flex: 1, maxWidth: "300px", ...style }}>
                <Select
                    options={filtrosGlobais.availableCorporations
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((corp) => ({
                        label: corp.name,
                        value: corp.id,
                    }))}
                    value={
                        filtrosGlobais.selectedCorporation && filtrosGlobais.availableCorporations.length > 0
                            ? {
                                label: filtrosGlobais.availableCorporations.find(
                                    corp => corp.id === filtrosGlobais.selectedCorporation
                                )?.name || '',
                                value: filtrosGlobais.selectedCorporation
                            }
                            : null
                    }
                    onChange={handleCorporationChange}
                    placeholder={placeholder}
                    isClearable
                    isDisabled={disabled}
                    classNamePrefix="react-select"
                />
            </div>
        );
    };

// Componente individual para apenas o filtro de organização
export const FiltroOrganizacao: React.FC<{
    onOrganizationChange?: (orgs: string[]) => void;
    disabled?: boolean;
    placeholder?: string;
    style?: React.CSSProperties;
}> = ({
    onOrganizationChange,
    disabled = false,
    placeholder = "Filtrar por Organização",
    style = {}
}) => {
        const {
            filtrosGlobais,
            setSelectedOrganizations,
            isInitialized
        } = useFiltrosGlobais();

        const handleOrganizationChange = (selectedOptions: any) => {
            const selectedOrgs = selectedOptions
                ? selectedOptions.map((opt: any) => opt.value)
                : [];

            setSelectedOrganizations(selectedOrgs);

            if (onOrganizationChange) {
                onOrganizationChange(selectedOrgs);
            }
        };

        // Filtrar organizações pela corporação selecionada
        const filteredOrganizations = filtrosGlobais.selectedCorporation
            ? filtrosGlobais.availableOrganizations.filter(
                org => org.Idcorporacao === filtrosGlobais.selectedCorporation
            )
            : filtrosGlobais.availableOrganizations;

        if (!isInitialized) {
            return (
                <div style={{ flex: 1, maxWidth: "300px", ...style }}>
                    <Select
                        placeholder="Carregando organizações..."
                        isDisabled={true}
                        classNamePrefix="react-select"
                    />
                </div>
            );
        }

        return (
            <div style={{ flex: 1, maxWidth: "300px", ...style }}>
                <Select
                    isMulti
                    options={filteredOrganizations.map((org) => ({
                        label: org.OrganizationName,
                        value: org.OrganizationName,
                    }))}
                    value={filteredOrganizations
                        .filter((org) => filtrosGlobais.selectedOrganizations.includes(org.OrganizationName))
                        .map((org) => ({ label: org.OrganizationName, value: org.OrganizationName }))}
                    onChange={handleOrganizationChange}
                    placeholder={placeholder}
                    isClearable
                    isDisabled={disabled}
                    classNamePrefix="react-select"
                />
            </div>
        );
    };

// Hook personalizado para acessar apenas os dados dos filtros
export const useFiltrosAtivos = () => {
    const { filtrosGlobais, isInitialized } = useFiltrosGlobais();

    return {
        selectedCorporation: filtrosGlobais.selectedCorporation,
        selectedOrganizations: filtrosGlobais.selectedOrganizations,
        availableOrganizations: filtrosGlobais.availableOrganizations,
        availableCorporations: filtrosGlobais.availableCorporations,
        isInitialized
    };
};

export default FiltroGlobal; 